/**
 * Front-end behavior for the mea/site-header hamburger + full-screen mobile
 * nav modal (shown at <= 1023px in place of the inline nav + cta, see
 * style.scss).
 *
 * Markup rendered by render.php:
 *
 *   <header class="mea-site-header">
 *     <div class="mea-site-header__bar mea-container">
 *       ...logo/nav/cta...
 *       <button class="mea-site-header__toggle" aria-expanded="false" aria-controls="{modal-id}">
 *     </div>
 *     <div id="{modal-id}" class="mea-site-header__modal" role="dialog" aria-modal="true" hidden>
 *       <button class="mea-site-header__modal-close">
 *       <nav class="mea-site-header__modal-nav">...links + cta...</nav>
 *     </div>
 *   </header>
 *
 * The toggle button opens the modal; the close button, Escape, or a click
 * on the modal backdrop (anywhere that isn't the nav content itself) close
 * it. While open, body scroll is locked, focus moves into the modal (to the
 * close button) and Tab is trapped inside it; on close, focus returns to
 * the toggle button.
 */
( function () {
	const FOCUSABLE_SELECTOR =
		'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

	// Deck slide 07 MOBILE: "Items stagger in from right, 100ms delay each."
	const MODAL_ITEM_STAGGER_MS = 100;

	function prefersReducedMotion() {
		return (
			window.matchMedia &&
			window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches
		);
	}

	function initHeader( header ) {
		const toggle = header.querySelector( '.mea-site-header__toggle' );
		if ( ! toggle ) {
			return;
		}

		const modalId = toggle.getAttribute( 'aria-controls' );
		const modal = modalId ? document.getElementById( modalId ) : null;
		if ( ! modal ) {
			return;
		}

		// Only the flagged instance gets the deck's 400ms slide-down modal
		// (style.scss); every other page keeps the original 200ms fade, so
		// the close-timeout has to match whichever is actually running or
		// `hidden` lands before the animation finishes and it visibly snaps.
		const isAnimated = header.matches( '[data-mea-anim="header"]' );
		const MODAL_CLOSE_MS = isAnimated ? 400 : 200;
		const modalItems = isAnimated
			? Array.prototype.slice.call(
					modal.querySelectorAll(
						'[data-mea-anim-el="header-modal-item"]'
					)
			  )
			: [];

		const closeBtn = modal.querySelector( '.mea-site-header__modal-close' );
		let previousBodyOverflow = '';
		let hideTimer = null;

		function isOpen() {
			return ! modal.hidden;
		}

		function getFocusable() {
			// `.filter()` drops anything inside a collapsed (inert)
			// accordion panel - `inert` already stops it from actually
			// receiving focus, but leaving it in this list would still let
			// the Tab-trap below treat it as the modal's first/last
			// focusable element and try (uselessly) to focus it.
			return Array.prototype.slice
				.call( modal.querySelectorAll( FOCUSABLE_SELECTOR ) )
				.filter( function ( el ) {
					return ! el.closest( '[inert]' );
				} );
		}

		// Mobile mega-menu accordion (2026-09-09): each `&__modal-item` with
		// a submenu renders its trigger as a <button> (see render.php's own
		// comment on this - no separate arrow, removed on request the same
		// day), which toggles the panel instead of navigating. Opening one
		// collapses any other open item, single-open like a standard
		// accordion. Height is animated via inline `max-height` (set from
		// the panel's own `scrollHeight`) rather than a CSS-only technique,
		// matching this file's existing preference for JS-timed transitions
		// (see `MODAL_CLOSE_MS` above) over guessing a fixed max-height.
		const accordionItems = Array.prototype.slice.call(
			modal.querySelectorAll( '.mea-site-header__modal-item' )
		);

		function collapseAccordionItem( item ) {
			const trigger = item.querySelector( '.mea-site-header__modal-link' );
			const submenu = item.querySelector(
				'.mea-site-header__modal-submenu'
			);
			if ( ! trigger || ! submenu ) {
				return;
			}
			item.classList.remove( 'is-expanded' );
			trigger.setAttribute( 'aria-expanded', 'false' );
			submenu.style.maxHeight = '';
			submenu.inert = true;
		}

		function collapseAllAccordionItems() {
			accordionItems.forEach( collapseAccordionItem );
		}

		accordionItems.forEach( function ( item ) {
			const trigger = item.querySelector( '.mea-site-header__modal-link' );
			const submenu = item.querySelector(
				'.mea-site-header__modal-submenu'
			);
			if ( ! trigger || ! submenu ) {
				return;
			}

			submenu.inert = true;

			trigger.addEventListener( 'click', function () {
				const wasExpanded = item.classList.contains( 'is-expanded' );
				collapseAllAccordionItems();
				if ( wasExpanded ) {
					return;
				}
				item.classList.add( 'is-expanded' );
				trigger.setAttribute( 'aria-expanded', 'true' );
				submenu.inert = false;
				submenu.style.maxHeight = submenu.scrollHeight + 'px';
			} );
		} );

		function lockScroll() {
			previousBodyOverflow = document.body.style.overflow;
			document.body.style.overflow = 'hidden';
		}

		function unlockScroll() {
			document.body.style.overflow = previousBodyOverflow;
		}

		function openModal() {
			if ( isOpen() ) {
				return;
			}

			if ( hideTimer ) {
				clearTimeout( hideTimer );
				hideTimer = null;
			}

			modalItems.forEach( function ( item, i ) {
				item.style.transitionDelay = i * MODAL_ITEM_STAGGER_MS + 'ms';
			} );

			modal.hidden = false;
			// Flush layout so removing `hidden` and adding `is-open` land in
			// separate frames, otherwise the opacity transition gets skipped.
			modal.getBoundingClientRect();
			modal.classList.add( 'is-open' );
			toggle.setAttribute( 'aria-expanded', 'true' );
			lockScroll();

			const target = closeBtn || getFocusable()[ 0 ];
			if ( target && typeof target.focus === 'function' ) {
				target.focus();
			}
		}

		function closeModal() {
			if ( ! isOpen() ) {
				return;
			}

			modal.classList.remove( 'is-open' );
			toggle.setAttribute( 'aria-expanded', 'false' );
			unlockScroll();
			// Reset so the menu always reopens fully collapsed rather than
			// remembering whichever category was last expanded.
			collapseAllAccordionItems();

			const finishClose = function () {
				modal.hidden = true;
				hideTimer = null;
			};

			if ( prefersReducedMotion() ) {
				finishClose();
			} else {
				// Matches the `.mea-site-header__modal` opacity (and, when
				// flagged, transform) transition duration in style.scss; a
				// timer (rather than waiting on `transitionend`) keeps this
				// correct even if the transition never fires (e.g. element
				// removed from the DOM elsewhere).
				hideTimer = setTimeout( finishClose, MODAL_CLOSE_MS );
			}

			if ( typeof toggle.focus === 'function' ) {
				toggle.focus();
			}
		}

		toggle.addEventListener( 'click', function () {
			if ( isOpen() ) {
				closeModal();
			} else {
				openModal();
			}
		} );

		if ( closeBtn ) {
			closeBtn.addEventListener( 'click', closeModal );
		}

		// Backdrop click: the modal root itself has no separate wrapper
		// element behind the nav, so a click that lands directly on the
		// modal (not on the nav/links/cta/close button inside it) counts
		// as a backdrop click.
		modal.addEventListener( 'click', function ( event ) {
			if ( event.target === modal ) {
				closeModal();
			}
		} );

		// Closing on nav/cta activation too, so the menu doesn't stay open
		// covering the page after the visitor picks a destination.
		modal.querySelectorAll( 'a' ).forEach( function ( link ) {
			link.addEventListener( 'click', closeModal );
		} );

		document.addEventListener( 'keydown', function ( event ) {
			if ( ! isOpen() ) {
				return;
			}

			if ( 'Escape' === event.key ) {
				closeModal();
				return;
			}

			if ( 'Tab' !== event.key ) {
				return;
			}

			// Trap focus inside the modal while it's open.
			const focusable = getFocusable();
			if ( ! focusable.length ) {
				return;
			}

			const first = focusable[ 0 ];
			const last = focusable[ focusable.length - 1 ];

			if ( event.shiftKey && document.activeElement === first ) {
				event.preventDefault();
				last.focus();
			} else if ( ! event.shiftKey && document.activeElement === last ) {
				event.preventDefault();
				first.focus();
			}
		} );

		// If the viewport crosses back into the desktop layout while the
		// modal is open (e.g. rotating a tablet, resizing dev tools), close
		// it so the now-visible inline nav isn't sitting behind a stale
		// full-screen overlay with no way to reopen it (the hamburger is
		// hidden again at that width).
		if ( window.matchMedia ) {
			const desktopQuery = window.matchMedia( '(min-width: 1024px)' );
			const handleDesktopChange = function ( event ) {
				if ( event.matches ) {
					closeModal();
				}
			};

			if ( typeof desktopQuery.addEventListener === 'function' ) {
				desktopQuery.addEventListener( 'change', handleDesktopChange );
			} else if ( typeof desktopQuery.addListener === 'function' ) {
				// Safari < 14 fallback.
				desktopQuery.addListener( handleDesktopChange );
			}
		}
	}

	function boot() {
		document.querySelectorAll( '.mea-site-header' ).forEach( initHeader );
	}

	if ( document.readyState !== 'loading' ) {
		boot();
	} else {
		document.addEventListener( 'DOMContentLoaded', boot );
	}
} )();
